import { PlaygroundParameters } from "./playground-parameters";
import { usePlaygroundPresentation } from "./playground-presentation";
import { toast } from "sonner";
import { SubmitHandler } from "react-hook-form";
import {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useId,
  useMemo,
} from "react";
import { ChatFormData, ChatInputProps } from "@/types";
import { Mention } from "@/types/agent-tools";
import { usePlaygroundParametersStore } from "@/atoms/playground";
import {
  EditableContent,
  EditableContentRef,
} from "./agent-tools/editable-content-test";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getDocuments, uploadDocument, deleteDocument } from "@/utils/axios-v2";
import { IDocument } from "@/types/conversations";
import { Paperclip, CircleAlert, LoaderCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useThemeMode } from "@/contexts/ThemeContext";
import { Tooltip } from "@/components/ui/tooltip-card";
import { useStoredTheme } from "@/utils/theme-context";
import { useConversationScope } from "@/hooks/useConversationScope";
import {
  conversationQueryKeys,
  conversationScopeIdentity,
  getConversationRequestScope,
  type ConversationScope,
} from "@/features/playground/conversation-query-keys";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { PLAYGROUND_UPLOAD_ACCEPT } from "@/utils/upload-file-types";
import {
  useDefaultModelsQuery,
  useModelsQuery,
} from "@/features/playground/hooks/useModelQueries";
import { resolveModelSelection } from "@/features/playground/model-selection";

const MAX_UPLOAD_FILES = 5;
const MAX_UPLOAD_FILE_SIZE = 20 * 1024 * 1024;

type LocalUploadState = "uploading" | "processing" | "ready" | "failed";

type LocalUploadItem = {
  id: string;
  filename: string;
  fileSize: number;
  contentType: string;
  state: LocalUploadState;
  error?: string;
};

const getUploadErrorMessage = (error: unknown, fallback: string) => {
  if (error && typeof error === "object") {
    const maybeAxiosError = error as {
      response?: { data?: { detail?: string; message?: string } };
      message?: string;
    };

    return (
      maybeAxiosError.response?.data?.detail ||
      maybeAxiosError.response?.data?.message ||
      maybeAxiosError.message ||
      fallback
    );
  }

  return fallback;
};

const getFileExtension = (fileName: string) => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : "";
};

const isAcceptedFileType = (file: File) => {
  return PLAYGROUND_UPLOAD_ACCEPT.split(",").some((accept) => {
    const acceptedType = accept.trim().toLowerCase();

    if (!acceptedType) return false;
    if (acceptedType.startsWith(".")) {
      return getFileExtension(file.name) === acceptedType;
    }
    if (acceptedType.endsWith("/*")) {
      return file.type
        .toLowerCase()
        .startsWith(acceptedType.replace("/*", "/"));
    }

    return file.type.toLowerCase() === acceptedType;
  });
};

const normalizeFileName = (name: string) => name.trim().toLowerCase();

const normalizeFileType = (type: string | null | undefined) =>
  (type || "").trim().toLowerCase();

const isDuplicateDocumentFile = (document: IDocument, file: File) => {
  const sameName =
    normalizeFileName(document.name) === normalizeFileName(file.name);
  if (!sameName) return false;

  const documentType = normalizeFileType(
    document.content_type || document.file_type,
  );
  const fileType = normalizeFileType(file.type);
  const sameKnownType = Boolean(
    documentType && fileType && documentType === fileType,
  );

  if (document.file_size == null) {
    return sameKnownType || !documentType || !fileType;
  }

  return Number(document.file_size) === file.size && sameKnownType;
};

const isDuplicateLocalUploadFile = (upload: LocalUploadItem, file: File) => {
  const sameName =
    normalizeFileName(upload.filename) === normalizeFileName(file.name);
  const sameSize = upload.fileSize === file.size;
  const uploadType = normalizeFileType(upload.contentType);
  const fileType = normalizeFileType(file.type);
  const sameType = uploadType === fileType;

  return sameName && sameSize && sameType;
};

const mapDocumentStatus = (status: string): LocalUploadState => {
  if (status === "complete") return "ready";
  if (status === "error") return "failed";
  if (status === "uploading") return "uploading";
  return "processing";
};

const normalizeDocuments = (data: unknown): IDocument[] => {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    const response = data as {
      documents?: unknown;
      data?: unknown;
      results?: unknown;
      items?: unknown;
    };

    if (Array.isArray(response.documents)) return response.documents;
    if (Array.isArray(response.data)) return response.data;
    if (Array.isArray(response.results)) return response.results;
    if (Array.isArray(response.items)) return response.items;
  }

  return [];
};

export default function ChatInput({
  onSubmit,
  formData,
  conversationId,
  confidentialMode = false,
  isLoading,
  stop,
  ...props
}: ChatInputProps & { stop: () => void }) {
  const { t } = useTranslation();
  const messageErrorId = useId();
  const modelsQuery = useModelsQuery();
  const defaultModelsQuery = useDefaultModelsQuery();
  const defaultSelectedModels = defaultModelsQuery.data;
  const storedTheme = useStoredTheme();
  const { modern } = usePlaygroundPresentation();
  const { mode: themeMode } = useThemeMode();
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false);
  const toolsMenuId = useId();
  const [isExternalUploading, setIsExternalUploading] = useState(false);
  const [localUploads, setLocalUploads] = useState<LocalUploadItem[]>([]);
  const [deletingDocumentIds, setDeletingDocumentIds] = useState<string[]>([]);
  const {
    setLlmConfigId,
    setPlannerLlmConfig,
    setMode,
    setTemperature,
    setMaxTokens,
    setLanguage,
  } = usePlaygroundParametersStore();

  const {
    handleSubmit,
    formState: { errors },
    resetField,
    getValues,
    watch,
    setValue,
  } = formData;

  const queryClient = useQueryClient();
  const { scope, identity, requestScope, ready } = useConversationScope();
  const currentIdentityRef = useRef(identity);
  const currentModelScopeIdentityRef = useRef(modelsQuery.identity);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editableContentRef = useRef<EditableContentRef>(null);
  // A return to A after visiting B is a new focus session, even for the same IDs.
  const focusOwner = useMemo(
    () => ({ identity, conversationId }),
    [identity, conversationId],
  );
  const focusOwnerRef = useRef<typeof focusOwner | null>(focusOwner);
  useLayoutEffect(() => {
    focusOwnerRef.current = focusOwner;
    return () => {
      focusOwnerRef.current = null;
    };
  }, [focusOwner]);
  const restoreAttachmentFocus = (
    trigger: HTMLButtonElement,
    form = trigger.closest("form"),
  ) => {
    if (focusOwnerRef.current !== focusOwner) return;
    if (
      document.activeElement !== trigger &&
      document.activeElement !== document.body
    )
      return;
    const upload = Array.from(
      form?.querySelectorAll<HTMLButtonElement>("[data-upload-attachment]") ??
        [],
    ).find((button) => !button.disabled && button.getClientRects().length > 0);
    (
      upload ?? form?.querySelector<HTMLElement>("[contenteditable=true]")
    )?.focus();
  };

  useLayoutEffect(() => {
    currentIdentityRef.current = identity;
    // Local upload/deletion rows are conversation-scope UI state and must be
    // detached before the browser can paint a newly selected request scope.
    // oxlint-disable-next-line react/set-state-in-effect
    setLocalUploads([]);
    // oxlint-disable-next-line react/set-state-in-effect
    setDeletingDocumentIds([]);
  }, [identity]);

  useLayoutEffect(() => {
    currentModelScopeIdentityRef.current = modelsQuery.identity;
  }, [modelsQuery.identity]);

  const { data: documents = [] } = useQuery({
    queryKey: conversationQueryKeys.documents(scope, conversationId ?? ""),
    queryFn: () => getDocuments(conversationId!, requestScope),
    refetchInterval: (query) => {
      const queryDocuments = normalizeDocuments(query.state.data);
      return queryDocuments.find(
        (doc) => doc.status != "complete" && doc.status != "error",
      )
        ? 5000
        : false;
    },
    select: normalizeDocuments,
    enabled: ready && !!conversationId && !confidentialMode,
  });

  useEffect(() => {
    if (!documents?.length) return;

    const documentNames = new Set(documents.map((doc) => doc.name));
    setLocalUploads((current) =>
      current.filter(
        (upload) =>
          upload.state === "failed" || !documentNames.has(upload.filename),
      ),
    );
  }, [documents]);

  const { mutate: deleteFile } = useMutation({
    mutationFn: (document_id: string) =>
      deleteDocument(conversationId!, document_id, requestScope),
    onMutate: (documentId) => {
      setDeletingDocumentIds((current) =>
        current.includes(documentId) ? current : [...current, documentId],
      );
      return { operationScope: scope };
    },
    onSuccess: (_data, documentId, context) => {
      const operationScope = context?.operationScope ?? scope;
      const documentsKey = conversationQueryKeys.documents(
        operationScope,
        conversationId!,
      );
      if (
        currentIdentityRef.current === conversationScopeIdentity(operationScope)
      ) {
        editableContentRef.current?.removeDocumentMention(documentId);
      }
      queryClient.setQueryData(documentsKey, (current) => {
        const normalizedDocuments = normalizeDocuments(current);
        return normalizedDocuments.filter((doc) => doc.id !== documentId);
      });
      queryClient.invalidateQueries({
        queryKey: documentsKey,
      });
    },
    onError: (error) => {
      console.error("Error deleting document:", error);
      toast.error(
        getUploadErrorMessage(error, t("playground.failedToRemoveDocument")),
      );
    },
    onSettled: (_data, _error, documentId) => {
      setDeletingDocumentIds((current) =>
        current.filter((id) => id !== documentId),
      );
    },
  });

  const updateLocalUpload = (
    id: string,
    updates: Partial<Omit<LocalUploadItem, "id" | "filename">>,
  ) => {
    setLocalUploads((current) =>
      current.map((upload) =>
        upload.id === id ? { ...upload, ...updates } : upload,
      ),
    );
  };

  const removeLocalUpload = (id: string) => {
    setLocalUploads((current) => current.filter((upload) => upload.id !== id));
  };

  const uploadFilesToConversation = async (
    targetConversationId: string,
    uploadRequests: Array<{ file: File; upload: LocalUploadItem }>,
  ) => {
    const operationScope: ConversationScope = scope;
    const operationRequestScope = getConversationRequestScope(operationScope);
    const documentsKey = conversationQueryKeys.documents(
      operationScope,
      targetConversationId,
    );
    await Promise.all(
      uploadRequests.map(async ({ file, upload }) => {
        try {
          updateLocalUpload(upload.id, {
            state: "uploading",
            error: undefined,
          });
          await uploadDocument(
            targetConversationId,
            file,
            operationRequestScope,
          );
          updateLocalUpload(upload.id, { state: "processing" });
          queryClient.invalidateQueries({
            queryKey: documentsKey,
          });
        } catch (error) {
          updateLocalUpload(upload.id, {
            state: "failed",
            error: getUploadErrorMessage(
              error,
              t("playground.failedToUploadDocument"),
            ),
          });
        }
      }),
    );

    queryClient.invalidateQueries({
      queryKey: documentsKey,
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files ?? []);

    if (selectedFiles.length === 0) {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    if (selectedFiles.length > MAX_UPLOAD_FILES) {
      toast.error(t("playground.fileUploadLimitExceeded"));
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    const attachedCount =
      (documents?.length || 0) +
      localUploads.filter((upload) => upload.state !== "failed").length;

    const uploadRequests: Array<{ file: File; upload: LocalUploadItem }> = [];
    const nextUploads: LocalUploadItem[] = [];
    const attachedDocuments = [...(documents || [])];
    const activeLocalUploads = localUploads.filter(
      (upload) => upload.state !== "failed",
    );

    selectedFiles.forEach((file) => {
      const uploadId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${file.name}-${file.size}-${Date.now()}-${Math.random()}`;

      const isDuplicate =
        attachedDocuments.some((document) =>
          isDuplicateDocumentFile(document, file),
        ) ||
        activeLocalUploads.some((upload) =>
          isDuplicateLocalUploadFile(upload, file),
        ) ||
        uploadRequests.some(({ file: queuedFile }) => {
          return (
            normalizeFileName(queuedFile.name) ===
              normalizeFileName(file.name) &&
            queuedFile.size === file.size &&
            normalizeFileType(queuedFile.type) === normalizeFileType(file.type)
          );
        });

      if (isDuplicate) {
        toast.error(
          t("playground.duplicateFileUpload", { fileName: file.name }),
        );
        return;
      }

      if (file.size > MAX_UPLOAD_FILE_SIZE) {
        nextUploads.push({
          id: uploadId,
          filename: file.name,
          fileSize: file.size,
          contentType: file.type,
          state: "failed",
          error: t("playground.fileSizeExceedsLimit"),
        });
        return;
      }

      if (!isAcceptedFileType(file)) {
        nextUploads.push({
          id: uploadId,
          filename: file.name,
          fileSize: file.size,
          contentType: file.type,
          state: "failed",
          error: t("playground.unsupportedFileType"),
        });
        return;
      }

      if (attachedCount + uploadRequests.length >= MAX_UPLOAD_FILES) {
        toast.error(t("playground.fileUploadLimitExceeded"));
        return;
      }

      const upload = {
        id: uploadId,
        filename: file.name,
        fileSize: file.size,
        contentType: file.type,
        state: "uploading",
      } satisfies LocalUploadItem;

      uploadRequests.push({ file, upload });
      nextUploads.push(upload);
    });

    setLocalUploads((current) => [...current, ...nextUploads]);

    if (uploadRequests.length > 0) {
      if (conversationId) {
        await uploadFilesToConversation(conversationId, uploadRequests);
      } else if (props.onFileUpload) {
        setIsExternalUploading(true);
        try {
          await props.onFileUpload(uploadRequests.map(({ file }) => file));
          uploadRequests.forEach(({ upload }) => {
            updateLocalUpload(upload.id, { state: "processing" });
          });
        } catch (error) {
          uploadRequests.forEach(({ upload }) => {
            updateLocalUpload(upload.id, {
              state: "failed",
              error: getUploadErrorMessage(
                error,
                t("playground.failedToUploadDocument"),
              ),
            });
          });
        } finally {
          setIsExternalUploading(false);
        }
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isFileUploading =
    isExternalUploading ||
    localUploads.some(
      (upload) => upload.state === "uploading" || upload.state === "processing",
    );
  const activeAttachmentCount =
    (documents?.length || 0) +
    localUploads.filter((upload) => upload.state !== "failed").length;
  const isAttachmentDisabled =
    confidentialMode ||
    isFileUploading ||
    activeAttachmentCount >= MAX_UPLOAD_FILES;

  const currentModel = watch("llm_config_id");
  const currentPlannerModel = watch("planner_llm_config");
  const currentMode = watch("mode");
  const models = modelsQuery.data;
  const currentScopeDefaults = defaultModelsQuery.data?.defaults;
  const isModelSelectionReady =
    modelsQuery.ready &&
    modelsQuery.isSuccess &&
    models?.some((model) => model.id === currentModel) &&
    (currentMode === "agentic" ||
      models.some((model) => model.id === currentPlannerModel));

  useEffect(() => {
    const initializationIdentity = modelsQuery.identity;
    if (
      !modelsQuery.ready ||
      !modelsQuery.isSuccess ||
      !defaultModelsQuery.isSuccess ||
      !currentScopeDefaults ||
      defaultModelsQuery.identity !== initializationIdentity ||
      currentModelScopeIdentityRef.current !== initializationIdentity ||
      !models ||
      models.length === 0
    ) {
      return;
    }

    const selection = resolveModelSelection({
      models,
      defaults: currentScopeDefaults,
      selectedModelId: currentModel,
      selectedPlannerModelId: currentPlannerModel,
    });

    if (selection.modelId && selection.modelId !== currentModel) {
      setValue("llm_config_id", selection.modelId);
      setLlmConfigId(selection.modelId);
    }

    if (
      selection.plannerModelId &&
      selection.plannerModelId !== currentPlannerModel
    ) {
      setValue("planner_llm_config", selection.plannerModelId);
      setPlannerLlmConfig(selection.plannerModelId);
    }
  }, [
    currentModel,
    currentPlannerModel,
    defaultModelsQuery.identity,
    defaultModelsQuery.isSuccess,
    currentScopeDefaults,
    models,
    modelsQuery.identity,
    modelsQuery.isSuccess,
    modelsQuery.ready,
    setLlmConfigId,
    setPlannerLlmConfig,
    setValue,
  ]);

  const submitMessage = (
    messageOverride?: string,
    mentionsOverride?: Mention[],
  ) => {
    if (!isModelSelectionReady || isFileUploading || isLoading) return;
    const data = getValues();
    const payload = {
      ...data,
      message: messageOverride ?? data.message,
      metadata: {
        ...data.metadata,
        mentions: mentionsOverride ?? data.metadata?.mentions ?? [],
      },
    };

    onSubmit?.(payload, []);
    resetField("message");
  };

  const onFormSubmit: SubmitHandler<ChatFormData> = (data) => {
    if (!isModelSelectionReady || isFileUploading || isLoading) return;
    onSubmit?.(data, []);
    resetField("message");
  };
  return (
    <form
      onSubmit={handleSubmit(onFormSubmit)}
      data-playground-composer={modern ? "app-shell" : "legacy"}
      className={modern ? "relative mx-auto flex w-full min-w-0 flex-col gap-4 rounded-lg border border-border bg-card px-3 py-4" : "relative mx-auto flex w-full max-w-[1000px] flex-col gap-2 rounded-[20px] p-2"}
      style={modern ? undefined : {
        backgroundColor: storedTheme.general.backgroundColor[themeMode],
      }}
    >
      {!confidentialMode &&
      ((documents && documents.length > 0) || localUploads.length > 0) ? (
        <div className="flex flex-wrap gap-2 px-2">
          {documents?.map((doc) => {
            const documentState = mapDocumentStatus(doc.status);
            const isRemovingDocument = deletingDocumentIds.includes(doc.id);

            return (
              <Tooltip
                key={doc.id}
                content={
                  documentState === "failed"
                    ? doc.error_message ||
                      t("playground.failedToUploadDocument")
                    : t("playground.typeAtToMention")
                }
              >
                <div
                  className="bg-muted"
                  style={{
                    display: "flex",
                    maxWidth: "156px",
                    minHeight: "24px",
                    padding: "3px 8px",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "6px",
                    borderRadius: "8px",
                    cursor: "help",
                  }}
                >
                  {documentState === "failed" && !isRemovingDocument && (
                    <CircleAlert
                      aria-hidden="true"
                      className="size-4 text-destructive dark:text-destructive-foreground"
                    />
                  )}
                  {documentState === "ready" && !isRemovingDocument && (
                    <Paperclip
                      aria-hidden="true"
                      className="size-4 text-[var(--color-99)]"
                    />
                  )}
                  {(isRemovingDocument ||
                    documentState === "uploading" ||
                    documentState === "processing") && (
                    <LoaderCircle
                      aria-hidden="true"
                      className="size-4 text-[var(--color-99)] animate-spin [animation-duration:2s] motion-reduce:animate-none"
                    />
                  )}
                  <span className="text-xs text-foreground truncate">
                    {doc.name}
                  </span>
                  {documentState === "failed" && (
                    <span role="alert" className="sr-only">
                      {doc.name}:{" "}
                      {doc.error_message ||
                        t("playground.failedToUploadDocument")}
                    </span>
                  )}
                  <span
                    role="status"
                    className="text-[10px] text-foreground/70"
                  >
                    {isRemovingDocument
                      ? t("common.removing")
                      : t(`playground.fileUploadStates.${documentState}`)}
                  </span>
                  <Button
                    size="icon"
                    type="button"
                    variant="ghost"
                    className="size-[26px] shrink-0 rounded-xl p-[5px] text-foreground/60 hover:bg-foreground/5 disabled:opacity-40"
                    disabled={isRemovingDocument}
                    aria-label={t("playground.removeAttachment", {
                      fileName: doc.name,
                    })}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent tooltip from interfering if needed, though usually fine
                      if (isRemovingDocument) return;
                      const trigger = e.currentTarget;
                      const ownerForm = trigger.closest("form");
                      deleteFile(doc.id, {
                        onSuccess: () =>
                          restoreAttachmentFocus(trigger, ownerForm),
                      });
                    }}
                  >
                    <X aria-hidden="true" className="size-4" />
                  </Button>
                </div>
              </Tooltip>
            );
          })}
          {localUploads.map((upload) => (
            <Tooltip
              key={upload.id}
              content={
                upload.state === "failed"
                  ? upload.error || t("playground.failedToUploadDocument")
                  : t(`playground.fileUploadStates.${upload.state}`)
              }
            >
              <div
                className="bg-muted"
                style={{
                  display: "flex",
                  maxWidth: "220px",
                  minHeight: "24px",
                  padding: "3px 8px",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "6px",
                  borderRadius: "8px",
                  cursor: upload.state === "failed" ? "help" : "default",
                }}
              >
                {upload.state === "failed" && (
                  <CircleAlert
                    aria-hidden="true"
                    className="size-4 text-destructive dark:text-destructive-foreground"
                  />
                )}
                {upload.state === "ready" && (
                  <Paperclip
                    aria-hidden="true"
                    className="size-4 text-[var(--color-99)]"
                  />
                )}
                {(upload.state === "uploading" ||
                  upload.state === "processing") && (
                  <LoaderCircle
                    aria-hidden="true"
                    className="size-4 text-[var(--color-99)] animate-spin [animation-duration:2s] motion-reduce:animate-none"
                  />
                )}
                <span className="text-xs text-foreground truncate">
                  {upload.filename}
                </span>
                {upload.state === "failed" && (
                  <span role="alert" className="sr-only">
                    {upload.filename}:{" "}
                    {upload.error || t("playground.failedToUploadDocument")}
                  </span>
                )}
                <span
                  role="status"
                  className="text-[10px] text-foreground/70"
                >
                  {t(`playground.fileUploadStates.${upload.state}`)}
                </span>
                {upload.state === "failed" && (
                  <Button
                    size="icon"
                    type="button"
                    variant="ghost"
                    className="size-[26px] shrink-0 rounded-xl p-[5px] text-foreground/60 hover:bg-foreground/5 disabled:opacity-40"
                    aria-label={t("playground.removeAttachment", {
                      fileName: upload.filename,
                    })}
                    onClick={(e) => {
                      e.stopPropagation();
                      restoreAttachmentFocus(e.currentTarget);
                      removeLocalUpload(upload.id);
                    }}
                  >
                    <X aria-hidden="true" className="size-4" />
                  </Button>
                )}
              </div>
            </Tooltip>
          ))}
          {documents && documents.length > 0 && (
            <div className="flex items-center ml-1 rtl:ml-0 rtl:mr-1">
              <span className="text-[10px] text-foreground/70 italic select-none">
                {t("playground.typeAtToMention")}
              </span>
            </div>
          )}
        </div>
      ) : null}

      <div className="relative">
        <EditableContent
          ref={editableContentRef}
          menuId={toolsMenuId}
          onMenuOpenChange={setToolsMenuOpen}
          conversationId={conversationId}
          onTextChange={(message) => {
            setValue("message", message);
          }}
          handleFormSubmit={(messageOverride, mentionsOverride) => {
            submitMessage(messageOverride, mentionsOverride);
          }}
          isStreaming={isLoading}
          isUploading={isFileUploading}
          isSubmitDisabled={!isModelSelectionReady}
          documents={documents}
          invalid={Boolean(errors.message)}
          errorMessageId={errors.message ? messageErrorId : undefined}
          stop={stop}
        />

        {!confidentialMode && (
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileChange}
            accept={PLAYGROUND_UPLOAD_ACCEPT}
            multiple
          />
        )}
      </div>

      {errors.message && (
        <p
          id={messageErrorId}
          role="alert"
          className="text-destructive dark:text-destructive-foreground text-sm mt-1 px-1"
        >
          {errors.message.message}
        </p>
      )}

      <PlaygroundParameters
        leftActions={
          <Tooltip content={t("playground.uploadDocumentTooltip")}>
            <Button
              size="icon"
              variant="ghost"
              data-upload-attachment
              aria-label={t("playground.uploadDocumentTooltip")}
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isAttachmentDisabled}
              className={`
            size-9 p-2 rounded-full
            text-muted-foreground hover:text-foreground hover:bg-foreground/10
            transition-all duration-200
            ${isAttachmentDisabled ? "opacity-50 cursor-not-allowed" : ""}
          `}
            >
              <Paperclip aria-hidden="true" className="size-5" />
            </Button>
          </Tooltip>
        }
        onModeChange={(mode) => {
          setValue("mode", mode);
          setMode(mode);
        }}
        mode={watch("mode")}
        selectedModel={watch("llm_config_id")}
        selectedPlannerModel={watch("planner_llm_config")}
        temperature={watch("temperature") || 0}
        maxTokens={watch("max_tokens") || 0}
        language={watch("metadata.language") || i18n.language || "en"}
        onModelChange={(modelId) => {
          setValue("llm_config_id", modelId);
          setLlmConfigId(modelId);
        }}
        onPlannerModelChange={(modelId) => {
          setValue("planner_llm_config", modelId);
          setPlannerLlmConfig(modelId);
        }}
        onTemperatureChange={(value) => {
          setValue("temperature", value);
          setTemperature(value);
        }}
        onMaxTokensChange={(value) => {
          setValue("max_tokens", value);
          setMaxTokens(value);
        }}
        onLanguageChange={(language) => {
          setValue("metadata.language", language);
          setLanguage(language);
        }}
        onOpenToolsMenu={(rect, trigger) =>
          editableContentRef.current?.openMenu(rect, trigger)
        }
        toolsMenuState={{
          open: toolsMenuOpen,
          id: toolsMenuId,
          disabled: !ready,
        }}
        defaultModels={defaultSelectedModels?.defaults}
      />
      {defaultModelsQuery.isError && (
        <p role="alert" className="px-2 text-sm text-destructive dark:text-destructive-foreground">
          {t("aiCatalog.failedToLoadModels")}
        </p>
      )}
    </form>
  );
}
